import { createContext, useContext, useMemo, useState } from 'react';

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface AuthContextValue {
  admin: AdminUser | null;
  token: string | null;
  login: (admin: AdminUser, token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  admin: null,
  token: null,
  login: () => undefined,
  logout: () => undefined
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(() => {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem('workloob-admin');
    return raw ? JSON.parse(raw) : null;
  });
  const [token, setToken] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : localStorage.getItem('workloob-token')
  );

  const handleLogin = (user: AdminUser, jwt: string) => {
    setAdmin(user);
    setToken(jwt);
    if (typeof window !== 'undefined') {
      localStorage.setItem('workloob-admin', JSON.stringify(user));
      localStorage.setItem('workloob-token', jwt);
    }
  };

  const handleLogout = () => {
    setAdmin(null);
    setToken(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('workloob-admin');
      localStorage.removeItem('workloob-token');
    }
  };

  const value = useMemo(
    () => ({
      admin,
      token,
      login: handleLogin,
      logout: handleLogout
    }),
    [admin, token]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

